/**
 * Historical Event Map — clustered event locations for a single app over a
 * chosen time range. Rendered as a GeoJSON circle layer (same approach as
 * the portfolio heatmap) so it stays fast even with hundreds of clusters.
 */
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { analyticsService, type EventLocation, type TimeRange } from '../services/analyticsService'
import { isMapboxTokenConfigured } from '../utils/mapbox'

interface HistoricalEventMapProps {
  timeRange: TimeRange
  bundleId: string | null | undefined
}

export default function HistoricalEventMap({ timeRange, bundleId }: HistoricalEventMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const popupRef = useRef<mapboxgl.Popup | null>(null)
  const loadedRef = useRef(false)
  const [mapReady, setMapReady] = useState(false)

  const mapboxToken = (import.meta.env.VITE_MAPBOX_TOKEN || '').trim()
  const hasMapboxToken = isMapboxTokenConfigured(mapboxToken)
  if (hasMapboxToken) {
    mapboxgl.accessToken = mapboxToken
  }

  const locationsQuery = useQuery({
    queryKey: ['app-event-locations', bundleId, timeRange],
    enabled: Boolean(bundleId) && hasMapboxToken,
    queryFn: () => analyticsService.getEventLocations(timeRange, { bundleId: bundleId!, limit: 500 }),
  })
  const locations = locationsQuery.data?.locations ?? []

  // ---------------------------------------------------------------------
  // Initialize map + layers once
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!hasMapboxToken || !containerRef.current || mapRef.current) return

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [-20, 25],
      zoom: 1.3,
      projection: { name: 'mercator' },
      attributionControl: false,
    })
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')

    map.on('load', () => {
      loadedRef.current = true
      setMapReady(true)
      map.resize()

      map.addSource('app-event-locations', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })

      map.addLayer({
        id: 'app-event-locations-point',
        type: 'circle',
        source: 'app-event-locations',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 1, 4, 500, 16],
          'circle-color': '#818cf8',
          'circle-opacity': 0.75,
          'circle-stroke-color': 'rgba(255,255,255,0.8)',
          'circle-stroke-width': 1,
        },
      })

      popupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        maxWidth: '260px',
      })

      map.on('mousemove', 'app-event-locations-point', (event) => {
        const feature = event.features?.[0]
        if (!feature || feature.geometry.type !== 'Point') return
        map.getCanvas().style.cursor = 'pointer'
        popupRef.current
          ?.setLngLat(event.lngLat)
          .setHTML(renderClusterPopup(feature.properties ?? {}))
          .addTo(map)
      })
      map.on('mouseleave', 'app-event-locations-point', () => {
        map.getCanvas().style.cursor = ''
        popupRef.current?.remove()
      })
    })

    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      popupRef.current = null
      loadedRef.current = false
      setMapReady(false)
    }
  }, [hasMapboxToken])

  // ---------------------------------------------------------------------
  // Push fetched clusters into the map whenever the range/data changes
  // ---------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !loadedRef.current) return

    const source = map.getSource('app-event-locations') as mapboxgl.GeoJSONSource | undefined
    if (!source) return

    source.setData(toFeatureCollection(locations))

    if (locations.length > 0) {
      const bounds = new mapboxgl.LngLatBounds()
      locations.forEach((location) => bounds.extend([location.lng, location.lat]))
      map.fitBounds(bounds, { padding: 48, maxZoom: 5, duration: 600 })
    }
  }, [locations, mapReady])

  return (
    <div className="relative h-full w-full overflow-hidden rounded-2xl">
      <div ref={containerRef} className="absolute inset-0" />

      {!hasMapboxToken && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm">
          <p className="px-4 text-center text-sm text-gray-300">
            Map is unavailable. Set `VITE_MAPBOX_TOKEN` to enable map tiles.
          </p>
        </div>
      )}

      {hasMapboxToken && locationsQuery.isLoading && (
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
          <p className="glass-pill inline-block px-4 py-2 text-sm text-gray-400">Loading event locations...</p>
        </div>
      )}

      {hasMapboxToken && !locationsQuery.isLoading && locations.length === 0 && (
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <p className="glass-pill inline-block px-4 py-2 text-sm text-gray-500">No location data for this period</p>
        </div>
      )}
    </div>
  )
}

function toFeatureCollection(locations: EventLocation[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: locations.map((location) => ({
      type: 'Feature',
      properties: {
        count: location.count,
        city: location.city,
        country: location.country,
        appsJson: JSON.stringify(location.apps ?? []),
      },
      geometry: { type: 'Point', coordinates: [location.lng, location.lat] },
    })),
  }
}

function renderClusterPopup(properties: Record<string, unknown>) {
  const count = Number(properties.count ?? 0)
  const city = typeof properties.city === 'string' ? properties.city : ''
  const country = typeof properties.country === 'string' ? properties.country : ''
  const place = [city, country].filter(Boolean).join(', ') || 'Unknown location'

  return `
    <div style="min-width:180px;border:1px solid rgba(255,255,255,0.14);border-radius:16px;background:rgba(3,7,18,0.94);padding:12px 14px;box-shadow:0 24px 80px rgba(0,0,0,0.5);backdrop-filter:blur(16px);">
      <div style="font-size:14px;font-weight:800;color:white;">${escapeHtml(place)}</div>
      <div style="margin-top:2px;font-size:12px;color:rgba(209,213,219,0.72);">${count.toLocaleString()} ${count === 1 ? 'event' : 'events'}</div>
    </div>
  `
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}
